import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { ts } from '../api';
import { Page, Section, Field, Select, useTS } from '../ui';

export default function TSFrontDesk() {
  const session = useTS();
  const company = session.company;
  const [fd, setFd] = useState({ ...(company.front_desk || {}) });
  const [st, setSt] = useState({ ...(company.settings || {}) });
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setFd({ ...fd, [k]: typeof v === 'object' && v?.target ? v.target.value : v });
  const setS = (k) => (v) => setSt({ ...st, [k]: typeof v === 'object' && v?.target ? v.target.value : v });

  const save = async () => {
    setBusy(true);
    try {
      await ts.updateSection('front_desk', fd);
      await ts.updateSection('settings', {
        unanswered_call_action: st.unanswered_call_action, after_hours_action: st.after_hours_action,
        unassigned_number_action: st.unassigned_number_action, ring_timeout_seconds: Number(st.ring_timeout_seconds) || 20,
        recording_enabled: !!st.recording_enabled, recording_consent_message: st.recording_consent_message,
      });
      await session.refresh();
      toast.success('Saved');
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const actions = [['ai_front_desk', 'AI front desk answers'], ['voicemail', 'Voicemail'], ['company_fallback', 'Ring the company fallback person']];
  return (
    <Page title="AI Front Desk" subtitle="Answers when your salesperson is offline, busy, doesn't pick up, or after hours."
      actions={<Button onClick={save} disabled={busy} data-testid="ts-frontdesk-save">Save</Button>}>
      <Section title="Assistant">
        <label className="flex items-center gap-2 text-sm"><Switch checked={!!fd.enabled} onCheckedChange={set('enabled')} /> Front desk on</label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Assistant name"><Input value={fd.assistant_name || ''} onChange={set('assistant_name')} /></Field>
          <Field label="Max conversation turns"><Input type="number" min={2} max={20} value={fd.max_turns || 8} onChange={e => set('max_turns')(Number(e.target.value))} /></Field>
        </div>
        <Field label="Greeting" hint="{company} and {assistant} are filled in for you."><Textarea rows={2} value={fd.greeting || ''} onChange={set('greeting')} /></Field>
        <Field label="After-hours greeting"><Textarea rows={2} value={fd.after_hours_greeting || ''} onChange={set('after_hours_greeting')} /></Field>
        <div className="flex flex-wrap gap-4 text-sm">
          <label className="flex items-center gap-2"><Switch checked={!!fd.transfer_enabled} onCheckedChange={set('transfer_enabled')} /> Offer to transfer to an available salesperson</label>
          <label className="flex items-center gap-2"><Switch checked={!!fd.take_messages} onCheckedChange={set('take_messages')} /> Take messages and create callbacks</label>
        </div>
        <p className="text-xs text-muted-foreground">Every front desk call creates or updates a lead and a callback for the right salesperson, with a summary.</p>
      </Section>
      <Section title="Call handling">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="When the salesperson doesn't answer"><Select value={st.unanswered_call_action} onChange={setS('unanswered_call_action')} options={actions} /></Field>
          <Field label="After business hours"><Select value={st.after_hours_action} onChange={setS('after_hours_action')} options={actions} /></Field>
          <Field label="Number with nobody assigned"><Select value={st.unassigned_number_action} onChange={setS('unassigned_number_action')} options={actions} /></Field>
          <Field label="Ring for (seconds)"><Input type="number" min={10} max={60} value={st.ring_timeout_seconds || 20} onChange={setS('ring_timeout_seconds')} /></Field>
        </div>
        <label className="flex items-center gap-2 text-sm"><Switch checked={!!st.recording_enabled} onCheckedChange={setS('recording_enabled')} /> Record calls</label>
        {st.recording_enabled && <Field label="Consent message callers hear first"><Input value={st.recording_consent_message || ''} onChange={setS('recording_consent_message')} /></Field>}
      </Section>
    </Page>
  );
}
